import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isNewPostCreation,
  saveDefaultApprovalSettings,
  setApprovalUI,
  setDefaultApprovalSettingsValue
} from '../../../../redux/slices/LomavisPostApproval/lomavisPostApproval';
import { lomavisShowToastr } from '../../../../redux/slices/utils/ui/toastr';
import UpgradeRequiredTooltipWrapper from '../../../utils/billing/UpgradeRequiredTooltipWrapper';
import { formatUsers } from '../../../LomavisPostApproval/components/userSelectTable/utils';
import { formatGroups } from '../../../LomavisPostApproval/components/groupSelectTable/utils';
import DefaultDropdown from './defaultDropdown';
import SettingRow from './settingRow';

const getErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (Array.isArray(data.non_field_errors)) return data.non_field_errors[0];

  const firstFieldError = Object.values(data).find((value: any) =>
    Array.isArray(value)
  ) as string[] | undefined;
  return firstFieldError?.[0] || fallback;
};

const getUserLabel = (user: any): string => {
  return user?.display_name || user?.name || user?.email || user?.uuid || '';
};

const getGroupLabel = (group: any, membersLabel: string): string => {
  const name = group?.name || group?.primary || group?.uuid || '';
  if (
    group?.member_count === undefined &&
    group?.members?.length === undefined
  ) {
    return name;
  }

  const memberCount = group?.member_count ?? group?.members?.length ?? 0;
  return `${name} (${memberCount} ${membersLabel})`;
};

// Plan gating comes from the subscription feature list the backend puts on every
// page (the same map the communication features are read from). Each approval
// target has its own flag because each combines the creator-defaults feature
// with a different approval feature.
export const CREATOR_ENABLE_DEFAULT_APPROVAL_USER =
  'CREATOR_ENABLE_DEFAULT_APPROVAL_USER';
export const CREATOR_ENABLE_DEFAULT_APPROVAL_GROUP =
  'CREATOR_ENABLE_DEFAULT_APPROVAL_GROUP';

const ApprovalDefaultSettingsModal: React.FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();
  const creatorState = useSelector((state: any) => state.lomavisCreator);
  const { locationUuid } = creatorState;
  const {
    data: {
      users,
      groups,
      location_uuid: approvalLocationUuid,
      cloud_uuid: approvalCloudUuid,
      is_multipost: isMultipost
    },
    default_settings: { loading, error, local_data, data: saved_data }
  } = useSelector((state: any) => state.lomavisPostApproval);

  const planFeatures = useSelector(
    (state: any) => state.userDetails?.userData?.feature_list_for_user
  );

  // The subscription feature list is the authority: the defaults endpoint
  // validates writes against the same plan flags, so a target reported as
  // unavailable here would be rejected on save anyway. featuresEnabled is not
  // used for this — it is only populated once the approval button is pressed,
  // which has not necessarily happened by the time these settings are opened.
  const userEnabled = Boolean(
    planFeatures?.[CREATOR_ENABLE_DEFAULT_APPROVAL_USER]
  );
  const groupsEnabled = Boolean(
    planFeatures?.[CREATOR_ENABLE_DEFAULT_APPROVAL_GROUP]
  );

  // Defaults only ever apply to a post that is being created, so editing an
  // existing post (or reusing one) must not even reach these settings.
  if (!locationUuid || !isNewPostCreation(creatorState)) return null;

  const resetLocalData = (settings = saved_data) => {
    dispatch(
      setDefaultApprovalSettingsValue({
        key: 'default_approval_user',
        value: settings?.default_approval_user || null
      })
    );
    dispatch(
      setDefaultApprovalSettingsValue({
        key: 'default_approval_group',
        value: settings?.default_approval_group || null
      })
    );
  };

  const closeModalWithSettings = (settings = saved_data) => {
    resetLocalData(settings);
    dispatch(setApprovalUI({ key: 'showDefaultSettingsModal', value: false }));
  };

  const closeModal = () => {
    closeModalWithSettings(saved_data);
  };

  // Clicking the selected row again clears it, so a default can be removed
  // without leaving the list. Picking one kind always clears the other.
  const handleUserSelect = (item: any) => {
    const user = item?.data || item;
    const isAlreadySelected =
      local_data.default_approval_user?.uuid === user?.uuid;

    dispatch(
      setDefaultApprovalSettingsValue({
        key: 'default_approval_user',
        value: isAlreadySelected ? null : user
      })
    );
    if (!isAlreadySelected) {
      dispatch(
        setDefaultApprovalSettingsValue({
          key: 'default_approval_group',
          value: null
        })
      );
    }
  };

  const handleGroupSelect = (item: any) => {
    const group = item?.data || item;
    const isAlreadySelected =
      local_data.default_approval_group?.uuid === group?.uuid;

    dispatch(
      setDefaultApprovalSettingsValue({
        key: 'default_approval_group',
        value: isAlreadySelected ? null : group
      })
    );
    if (!isAlreadySelected) {
      dispatch(
        setDefaultApprovalSettingsValue({
          key: 'default_approval_user',
          value: null
        })
      );
    }
  };

  const clearDefaults = () => {
    dispatch(
      setDefaultApprovalSettingsValue({
        key: 'default_approval_user',
        value: null
      })
    );
    dispatch(
      setDefaultApprovalSettingsValue({
        key: 'default_approval_group',
        value: null
      })
    );
  };

  const saveSettings = async () => {
    try {
      const settings = await dispatch(saveDefaultApprovalSettings(locationUuid));
      lomavisShowToastr(t('lomavisCreator.defaultSettings.saved'), 'success');
      closeModalWithSettings(settings);
    } catch (saveError) {
      lomavisShowToastr(
        t('lomavisCreator.submitError'),
        'error'
      );
    }
  };

  const selectedUser = local_data.default_approval_user || null;
  const selectedGroup = local_data.default_approval_group || null;

  const formattedUsers = formatUsers(users, {
    location_uuid: approvalLocationUuid,
    cloud_uuid: approvalCloudUuid,
    is_multipost: isMultipost
  });
  const formattedGroups = formatGroups(groups, t('members'));

  // A saved default whose user/group is no longer selectable still has to show
  // up, otherwise the modal claims no default while the backend has one.
  const activeUser = selectedUser
    ? formattedUsers.find((user: any) => user.uuid === selectedUser.uuid) || {
        uuid: selectedUser.uuid,
        primary: getUserLabel(selectedUser),
        secondary: '',
        initials: selectedUser.initials,
        color: selectedUser.user_color
      }
    : null;

  const activeGroup = selectedGroup
    ? formattedGroups.find((group: any) => group.uuid === selectedGroup.uuid) || {
        uuid: selectedGroup.uuid,
        primary: getGroupLabel(selectedGroup, t('members')),
        secondary: '',
        initials: selectedGroup.initials,
        color: selectedGroup.group_color
      }
    : null;

  const activeDefault = activeUser || activeGroup;

  return (
    <Modal show onHide={closeModal} size="lg" centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title>
          <i className="fa-duotone fa-gear me-2"></i>
          {t('lomavisCreator.defaultSettings.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-0">
        {error && (
          <div className="alert alert-danger mb-3">
            {getErrorMessage(error, t('lomavisCreator.submitError'))}
          </div>
        )}

        {/* What these settings do */}
        <p className="text-muted small">
          {t('lomavisCreator.defaultSettings.intro')}
        </p>

        <div className="separator mb-2"></div>

        <SettingRow
          label={t('lomavisCreator.defaultSettings.approval.user')}
          helpText={t('lomavisCreator.defaultSettings.approval.userHelpText')}
        >
          <UpgradeRequiredTooltipWrapper show={!userEnabled} trigger="click">
            <div>
              <DefaultDropdown
                items={formattedUsers}
                active={activeUser}
                onPick={handleUserSelect}
                onClear={clearDefaults}
                noneLabel={t('lomavisCreator.defaultSettings.none')}
                emptyLabel={t(
                  'lomavisCreator.defaultSettings.approval.noUsers'
                )}
                disabled={loading}
                locked={!userEnabled}
              />
            </div>
          </UpgradeRequiredTooltipWrapper>
        </SettingRow>

        <div className="separator"></div>

        <SettingRow
          label={t('lomavisCreator.defaultSettings.approval.group')}
          helpText={t('lomavisCreator.defaultSettings.approval.groupHelpText')}
        >
          <UpgradeRequiredTooltipWrapper show={!groupsEnabled} trigger="click">
            <div>
              <DefaultDropdown
                items={formattedGroups}
                active={activeGroup}
                onPick={handleGroupSelect}
                onClear={clearDefaults}
                noneLabel={t('lomavisCreator.defaultSettings.none')}
                emptyLabel={t(
                  'lomavisCreator.defaultSettings.approval.noGroups'
                )}
                disabled={loading}
                locked={!groupsEnabled}
              />
            </div>
          </UpgradeRequiredTooltipWrapper>
        </SettingRow>
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-between">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={clearDefaults}
          disabled={loading || !activeDefault}
        >
          <i className="fas fa-times me-2"></i>
          {t('lomavisCreator.defaultSettings.clear')}
        </button>
        <div>
          <button
            type="button"
            className="btn btn-secondary btn-sm me-2"
            onClick={closeModal}
            disabled={loading}
          >
            {t('lomavisPostApproval.requestApproval.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={saveSettings}
            disabled={loading}
          >
            {loading ? (
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <i className="far fa-save me-2"></i>
            )}
            {t('lomavisPostApproval.requestApproval.save')}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ApprovalDefaultSettingsModal;
